import React, { useEffect, useState } from 'react';
import { Plus, PencilSimple, Trash, X, FloppyDisk, CircleNotch, Snowflake, Sun, CloudSun } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';

const API = `${window.location.origin}/api`;
export const SEZONY = {
  zima: { label: 'Zima', Icon: Snowflake, cls: 'bg-blue-100 text-blue-700' },
  lato: { label: 'Lato', Icon: Sun, cls: 'bg-amber-100 text-amber-700' },
  caloroczne: { label: 'Całoroczne', Icon: CloudSun, cls: 'bg-emerald-100 text-emerald-700' },
};
const vLabel = (p) => vehicleLabel(p, p ? `Pojazd (${String(p.id || '').slice(0, 6)})` : '—');

function OponyModal({ item, vehicles, onClose, onSaved }) {
  const [form, setForm] = useState({
    pojazd_id: item?.pojazd_id || vehicles[0]?.id || '',
    rozmiar: item?.rozmiar || '',
    sezon: item?.sezon || 'zima',
    notes: item?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.pojazd_id || !form.rozmiar.trim()) return alert('Wybierz pojazd i wpisz rozmiar');
    setSaving(true);
    try {
      if (item) await axios.put(`${API}/opony/${item.id}`, form);
      else await axios.post(`${API}/opony`, form);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || 'Błąd zapisu'); }
    finally { setSaving(false); }
  };
  const field = 'mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="opony-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">{item ? 'Edytuj opony' : 'Dodaj opony'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="opony-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block text-sm font-medium text-slate-700">Pojazd
          <select value={form.pojazd_id} onChange={(e) => set('pojazd_id', e.target.value)} className={field} data-testid="opony-pojazd-select">
            {vehicles.map((v) => <option key={v.id} value={v.id}>{vLabel(v)}</option>)}
          </select>
        </label>
        <label className="block text-sm font-medium text-slate-700">Rozmiar
          <input value={form.rozmiar} onChange={(e) => set('rozmiar', e.target.value)} placeholder="np. 225/65 R16C" className={field} data-testid="opony-rozmiar-input" />
        </label>
        <label className="block text-sm font-medium text-slate-700">Sezon
          <select value={form.sezon} onChange={(e) => set('sezon', e.target.value)} className={field} data-testid="opony-sezon-select">
            {Object.entries(SEZONY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block text-sm font-medium text-slate-700">Uwagi (opcjonalnie)
          <input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="np. marka, bieżnik" className={field} data-testid="opony-notes-input" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md bg-slate-100 hover:bg-slate-200" data-testid="opony-cancel">Anuluj</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-slate-900 text-white font-semibold disabled:opacity-60" data-testid="opony-save">
            {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />} Zapisz
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OponyTab({ isAdmin = false }) {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | item

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [axios.get(`${API}/opony`)];
      if (isAdmin) reqs.push(axios.get(`${API}/pojazdy`));
      const [o, p] = await Promise.all(reqs);
      setItems(o.data || []);
      if (p) setVehicles(p.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const remove = async (it) => {
    if (!window.confirm(`Usunąć opony ${it.rozmiar} (${SEZONY[it.sezon]?.label}) z ${vLabel(it.pojazd)}?`)) return;
    try { await axios.delete(`${API}/opony/${it.id}`); load(); } catch (e) { alert(e.response?.data?.detail || 'Błąd'); }
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.pojazd_id] = acc[it.pojazd_id] || { pojazd: it.pojazd, list: [] }).list.push(it);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="opony-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Opony</h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">{isAdmin ? 'Komplety opon przypisane do pojazdów.' : 'Opony w Twoich pojazdach.'}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setModal('new')} disabled={vehicles.length === 0} className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-semibold disabled:opacity-50" data-testid="opony-add-btn">
            <Plus className="w-4 h-4" weight="bold" /> Dodaj opony
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white rounded-lg border border-slate-200" data-testid="opony-empty">Brak zapisanych opon.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(grouped).map(([pid, g]) => (
            <div key={pid} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`opony-pojazd-${pid}`}>
              <div className="font-bold text-slate-800 mb-2">{vLabel(g.pojazd)}</div>
              <div className="space-y-2">
                {g.list.map((it) => {
                  const S = SEZONY[it.sezon] || SEZONY.zima;
                  return (
                    <div key={it.id} className="flex items-center gap-3 border border-slate-100 rounded-md px-3 py-2" data-testid={`opony-row-${it.id}`}>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${S.cls}`}><S.Icon className="w-3.5 h-3.5" weight="bold" /> {S.label}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-800">{it.rozmiar}</div>
                        {it.notes && <div className="text-xs text-slate-500 truncate">{it.notes}</div>}
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setModal(it)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edytuj" data-testid={`opony-edit-${it.id}`}><PencilSimple className="w-4 h-4" /></button>
                          <button onClick={() => remove(it)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Usuń" data-testid={`opony-delete-${it.id}`}><Trash className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <OponyModal
          item={modal === 'new' ? null : modal}
          vehicles={vehicles}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
